import React from "react";
import PageShell from "@/components/PageShell";
import {
  LifeBuoy,
  Mail,
  Clock,
  MessageSquare,
  Bug,
  FileQuestion,
  ShieldCheck,
} from "lucide-react";

const SUPPORT_EMAIL = "support@myspendnova.com";

const InfoCard = ({ icon: Icon, title, text }) => (
  <div className="sn-glass-dark rounded-2xl p-5 flex gap-4 items-start">
    <div
      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
      style={{
        background:
          "linear-gradient(140deg, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.06) 100%)",
        border: "1px solid rgba(34,197,133,0.35)",
      }}
    >
      <Icon className="text-[#22C585]" size={20} />
    </div>
    <div className="min-w-0">
      <h4
        className="font-semibold text-white"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        {title}
      </h4>
      <p className="text-sm text-[#C7D0DF] mt-1 leading-relaxed">{text}</p>
    </div>
  </div>
);

const Support = () => {
  return (
    <PageShell navyTheme={true}>
      <section className="relative">
        <div className="mx-auto max-w-4xl px-4 py-8 md:py-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[#22C585] font-semibold">
            Support
          </div>
          <h1
            className="mt-2 text-4xl md:text-5xl font-extrabold text-white leading-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
            data-testid="support-title"
          >
            We're here to help.
          </h1>
          <p className="mt-3 text-[#C7D0DF] max-w-2xl">
            Questions about SpendNova™, feature requests, bug reports, or App
            Store / Play Store reviewer inquiries — reach the team directly at
            the email below.
          </p>

          {/* PROMINENT DEVELOPER CONTACT — for app-store reviewers */}
          <div
            className="mt-8 rounded-3xl p-6 md:p-10"
            style={{
              background:
                "linear-gradient(135deg, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.06) 100%)",
              border: "1px solid rgba(34,197,133,0.45)",
              boxShadow:
                "0 0 0 1px rgba(34,197,133,0.12) inset, 0 30px 60px -30px rgba(34,197,133,0.35)",
            }}
            data-testid="support-primary-contact"
          >
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{
                  background: "rgba(34,197,133,0.22)",
                  border: "1px solid rgba(34,197,133,0.55)",
                }}
              >
                <LifeBuoy size={26} className="text-[#22C585]" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold"
                >
                  Developer Contact
                </div>
                <div
                  className="mt-2 flex items-center gap-3 text-white"
                  data-testid="support-contact-row"
                >
                  <Mail size={22} className="text-[#22C585] shrink-0" />
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="font-extrabold tracking-tight hover:text-[#22C585] transition-colors"
                    style={{
                      display: "inline-block",
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      wordBreak: "normal",
                      overflowWrap: "normal",
                      fontFamily: "Sora, Inter, sans-serif",
                      fontSize: "clamp(20px, 3.4vw, 34px)",
                      lineHeight: 1.15,
                    }}
                    data-testid="support-contact-email"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </div>
                <p className="mt-3 text-sm text-[#C7D0DF] leading-relaxed">
                  This inbox is monitored by the SpendNova™ engineering team.
                  We reply to every message, typically within{" "}
                  <strong className="text-white">1 business day</strong>.
                </p>
                <div className="mt-4">
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=SpendNova%20Support%20Request`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-transform hover:-translate-y-[1px]"
                    style={{
                      background: "#22C585",
                      boxShadow:
                        "0 10px 30px -12px rgba(34,197,133,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
                    }}
                    data-testid="support-contact-button"
                  >
                    <Mail size={16} />
                    Email support
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <InfoCard
              icon={Clock}
              title="Response window"
              text="Weekdays: within 1 business day. Weekends & U.S. federal holidays: within 48 hours."
            />
            <InfoCard
              icon={Bug}
              title="Report a bug"
              text="Include your device model, OS version, and repro steps so we can fix it fast."
            />
            <InfoCard
              icon={FileQuestion}
              title="App-store reviewers"
              text="This email is our official developer support channel for Apple and Google review teams."
            />
          </div>

          <article
            className="sn-glass-strong-dark rounded-3xl p-6 md:p-10 mt-10 sn-prose-dark"
            data-testid="support-content"
          >
            <h2>What to include in your message</h2>
            <p>
              To help us resolve your request on the first reply, please include
              the following whenever it's relevant:
            </p>
            <ul>
              <li>
                <strong>Subject line</strong> — a short summary
                (e.g. "PCS calculator: wrong per-diem for Fort Bragg").
              </li>
              <li>
                <strong>Device</strong> — iPhone / iPad / Android model and OS
                version.
              </li>
              <li>
                <strong>App version</strong> — found under Settings → About
                (once the app is live). For web usage, include the browser and
                version.
              </li>
              <li>
                <strong>Steps to reproduce</strong> — what you tapped, what you
                expected, what happened instead.
              </li>
              <li>
                <strong>Screenshots</strong> — feel free to attach up to 5 images
                per message.
              </li>
            </ul>

            <h2>Other ways to reach us</h2>
            <p>
              For privacy or data-handling questions specifically, you can also
              email{" "}
              <a href="mailto:privacy@myspendnova.com">privacy@myspendnova.com</a>
              . For everything else — feature requests, bug reports, and
              reviewer inquiries — use{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
            <p>
              For corporate inquiries — partnerships, media, and matters
              directed to FetchDay LLC — email{" "}
              <a href="mailto:admin@myspendnova.com" data-testid="support-admin-email">
                admin@myspendnova.com
              </a>
              .
            </p>

            <div
              className="mt-8 rounded-2xl p-5 flex items-center gap-3"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <ShieldCheck className="text-[#22C585] shrink-0" size={20} />
              <div className="text-sm text-white">
                Support conversations are handled with the same{" "}
                <strong>Bunker Security</strong> discipline as the rest of
                SpendNova™.
              </div>
            </div>

            <div
              className="mt-5 rounded-2xl p-5 flex items-center gap-3"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <MessageSquare className="text-[#22C585] shrink-0" size={20} />
              <div className="text-sm text-white">
                We read every email. No auto-responders, no ticket queues that
                trap your message.
              </div>
            </div>
          </article>
        </div>
      </section>
    </PageShell>
  );
};

export default Support;
